import type { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { seoOpenGraphNodes } from "@/components/SeoOpenGraph";
import { SEO_BRAND, canonicalUrl, OG_IMAGE_URL, PAGE_OG_IMAGES, trustTwitterMeta } from "@/lib/seoConstants";
import { buildHomePageSchemas } from "@/lib/seoSchemas";
import type { AppLang } from "@/locale/appLang";
import {
  HREFLANG_BY_LANG,
  INDEXABLE_LANGS,
  htmlLangForLang,
  isCardLang,
  ogLocaleForLang,
} from "@/locale/appLang";
import { localizedPath, parseLocalePath } from "@/locale/routes";

export type SeoHeadProps = {
  /** Путь без префикса локали, например `/services/retainer` или `/`. */
  basePath: string;
  title: string;
  description: string;
  keywords?: string;
  lang: AppLang;
  robots?: string;
  /** Переопределение og:image; по умолчанию — из PAGE_OG_IMAGES или OG_IMAGE_URL. */
  ogImage?: string;
  /** Organization + WebSite + LocalBusiness — только главная. */
  includeHomeSchemas?: boolean;
  children?: ReactNode;
};

const GEO_BY_LANG: Partial<Record<AppLang, { region: string; placename: string }>> = {
  uk: { region: "UA-30", placename: "Kyiv" },
  kk: { region: "KZ-71", placename: "Astana" },
  uz: { region: "UZ-TK", placename: "Tashkent" },
  ky: { region: "KG-G", placename: "Bishkek" },
};

function hreflangNodes(basePath: string) {
  const ruUrl = canonicalUrl(localizedPath(basePath, "ru"));
  return (
    <>
      {INDEXABLE_LANGS.flatMap((lang) => {
        const href = canonicalUrl(localizedPath(basePath, lang));
        return HREFLANG_BY_LANG[lang].map((hrefLang) => (
          <link key={hrefLang} rel="alternate" hrefLang={hrefLang} href={href} />
        ));
      })}
      <link rel="alternate" hrefLang="x-default" href={ruUrl} />
    </>
  );
}

export function SeoHead({
  basePath,
  title,
  description,
  keywords,
  lang,
  robots,
  ogImage,
  includeHomeSchemas = false,
  children,
}: SeoHeadProps) {
  const { pathname } = useLocation();
  const canonical = canonicalUrl(pathname);
  const normalizedBase = basePath.startsWith("/") ? basePath : `/${basePath}`;
  const { basePath: parsedBase } = parseLocalePath(pathname);
  const hrefBase = normalizedBase === parsedBase ? normalizedBase : parsedBase;
  const trust = trustTwitterMeta(lang);
  const defaultRobots = isCardLang(lang)
    ? "noindex, follow"
    : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1";
  const resolvedRobots = robots ?? defaultRobots;
  const resolvedOgImage = ogImage ?? PAGE_OG_IMAGES[hrefBase] ?? OG_IMAGE_URL;
  const geo = GEO_BY_LANG[lang];

  return (
    <Helmet>
      <html lang={htmlLangForLang(lang)} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="author" content={SEO_BRAND} />
      <meta name="application-name" content={SEO_BRAND} />
      {keywords ? <meta name="keywords" content={keywords} /> : null}
      <meta name="twitter:label1" content={trust.label1} />
      <meta name="twitter:data1" content={trust.data1} />
      <meta name="twitter:label2" content={trust.label2} />
      <meta name="twitter:data2" content={trust.data2} />
      <meta name="language" content={lang} />
      {geo ? (
        <>
          <meta name="geo.region" content={geo.region} />
          <meta name="geo.placename" content={geo.placename} />
        </>
      ) : null}
      <link rel="canonical" href={canonical} />
      {hreflangNodes(hrefBase)}
      <meta name="robots" content={resolvedRobots} />
      {seoOpenGraphNodes({
        path: localizedPath(hrefBase, lang),
        title,
        description,
        ogLocale: ogLocaleForLang(lang),
        ogImage: resolvedOgImage,
      })}
      {includeHomeSchemas
        ? buildHomePageSchemas(lang).map((schema, index) => (
            <script key={`home-schema-${index}`} type="application/ld+json">
              {JSON.stringify(schema)}
            </script>
          ))
        : null}
      {children}
    </Helmet>
  );
}
